import { AnyObject } from "immer/dist/internal";
import { SessionDescriptionHandlerOptions, Web } from "sip.js";
import { SimpleUser } from "./simple-user";
import * as helpers from "../../../util/helpers";
import { toAbsoluteUrl } from "../../../../Utils/_helpers";
import broadcastEvents from "./broadcast-events";

let isLogEnabled = false;
const LOG_LEVEL_NONE = "LOG_LEVEL_NONE";
const LOG_LEVEL_BASIC = "LOG_LEVEL_BASIC";
const LOG_LEVEL_DEBUG = "LOG_LEVEL_DEBUG";
const LOG_LEVEL_ERROR = "LOG_LEVEL_ERROR";

let bob: any;
var userConfig: any = {};
let notifyCallEvent: any = false;
let notifyEventBasedCallEvent: any = false;
let isEventBasedCallEnabled: any = false
let allEventsInfo: any = {};
(window as any)['knowlarityNotificationNotSupported'] = true;
(window as any)['knowlarityNotificationFlag'] = true;

const app_log = (level: string, message: any) => {
  if (level === LOG_LEVEL_NONE) {
    return;
  }
  if (
    level === LOG_LEVEL_DEBUG &&
    userConfig &&
    userConfig["log_level"] === level
  ) {
    return;
  }

  if (level === LOG_LEVEL_ERROR) {
    console.error("[webrtc][softphone.js][" + level + "] ::");
    console.error(message);
    return;
  }

  if (level === LOG_LEVEL_BASIC) {
  }
};

const app_alert = (level: string, message: string) => {
  if (!isLogEnabled) return;
  app_log(level, message);
};

export function setText(id: string, text: string): any {
  return;
}

export function show(id: string): any {}

export function hide(id: string): any {}

function extractHeaders(headers: Record<string, any>, key: any): Record<string, string> {
  // Check if the key exists in the allEventsInfo object
  if (!(key in allEventsInfo)) {
    allEventsInfo[key] = {};
  }

  // Iterate over the headers
  Object.keys(headers).forEach((headerName) => {
    const headerValue = headers[headerName][0].raw; // Assuming the headers are always an array with a single value
    allEventsInfo[key][headerName] = headerValue;
  });

  return allEventsInfo;
}

class SimpleUserWithDataChannel extends SimpleUser {
  private _dataChannel: RTCDataChannel | undefined;

  constructor(server: string, options: Web.SimpleUserOptions = {}) {
    super(server, options);
  }

  public get dataChannel(): RTCDataChannel | undefined {
    return this._dataChannel;
  }

  public set dataChannel(dataChannel: RTCDataChannel | undefined) {
    this._dataChannel = dataChannel;
    if (!dataChannel) {
      return;
    }
    dataChannel.onclose = (event) => {};
    dataChannel.onerror = (event: AnyObject) => {
      app_log(LOG_LEVEL_ERROR, `[${this.id}] data channel onError`);
      app_log(LOG_LEVEL_ERROR, event.error);
      app_alert(
        LOG_LEVEL_DEBUG,
        `[${this.id}] Data channel error.\n` + event.error
      );
    };
    dataChannel.onmessage = (event) => {
      const el = document.createElement("p");
      el.classList.add("message");
      const node = document.createTextNode(event.data);
      el.appendChild(node);
    };
    dataChannel.onopen = (event) => {};
  }

  public send(): void {
    if (!this.dataChannel) {
      const error = "No data channel";
      app_log(LOG_LEVEL_ERROR, `[${this.id}] failed to send message`);
      app_log(LOG_LEVEL_ERROR, error);
      app_alert(
        LOG_LEVEL_DEBUG,
        `[${this.id}] Failed to send message.\n` + error
      );
      return;
    }
    const msg = "";
    switch (this.dataChannel.readyState) {
      case "connecting":
        app_log(
          LOG_LEVEL_ERROR,
          "Attempted to send message while data channel connecting."
        );
        break;
      case "open":
        try {
          this.dataChannel.send(msg);
        } catch (error) {
          app_log(LOG_LEVEL_ERROR, `[${this.id}] failed to send message`);
          app_log(LOG_LEVEL_ERROR, error);
          app_alert(
            LOG_LEVEL_DEBUG,
            `[${this.id}] Failed to send message.\n` + error
          );
        }
        break;
      case "closing":
        app_log(
          LOG_LEVEL_ERROR,
          "Attempted to send message while data channel closing."
        );
        break;
      case "closed":
        app_log(
          LOG_LEVEL_ERROR,
          "Attempted to send while data channel connection closed."
        );
        break;
    }
  }
}

export function getAudio(id: string): HTMLAudioElement {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLAudioElement)) {
    throw new Error(`Element "${id}" not found or not an audio element.`);
  }
  return el;
}

function buildUser(
  webSocketServer: string,
  aor: string,
  displayName: string,
  password: string,
  iceServers: any
): SimpleUser {
  const options: Web.SimpleUserOptions = {
    reconnectionAttempts: 1,
    aor,
    media: {
      remote: {
        audio: getAudio("remoteAudio"),
      },
    },
    userAgentOptions: {
      logLevel: !isLogEnabled
        ? "error"
        : userConfig && userConfig["log_level"] === LOG_LEVEL_DEBUG
        ? "debug"
        : "error",
      displayName,
      authorizationPassword: password,
      sessionDescriptionHandlerFactoryOptions:{
        peerConnectionConfiguration:{
          iceServers: iceServers,
        }
      }
    },
  };

  const user = new SimpleUserWithDataChannel(webSocketServer, options);

  const delegate: Web.SimpleUserDelegate = {
    onCallCreated: makeCallCreatedCallback(user),
    onCallReceived: makeCallReceivedCallback(user),
    onCallHangup: makeCallHangupCallback(user),
    onRegistered: makeRegisteredCallback(user),
    onUnregistered: makeUnregisteredCallback(user),
    onServerConnect: makeServerConnectCallback(user),
    onServerDisconnect: makeServerDisconnectCallback(user),
    onSomeInfo: (info: any) => makeCallInfoCallback(user, info)
  } as any;
  user.delegate = delegate;

  const connectAndRegister = () => {
    user
      .connect()
      .then(() => {
        user
          .register(undefined, {
            requestDelegate: {
              onReject: (response: { message: { reasonPhrase: any } }) => {
                //console.warn(`[${user.id}] REGISTER rejected`);
                let message = `Registration of "${user.id}" rejected.\n`;
                message += `Reason: ${response.message.reasonPhrase}\n`;
                app_alert(LOG_LEVEL_DEBUG, message);
              },
            },
          })
          .then(() => {
            setText("sip-status", "SIP Registered");
            broadcastEvents("CONNECT", '', '');
            notifyCallEvent({
              type: "registered",
              payload: { user: user, softphone: softphone },
            });
          })
          .catch((error: Error) => {
            app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to register`);
            app_log(LOG_LEVEL_ERROR, error);
            app_alert(
              LOG_LEVEL_DEBUG,
              `[${user.id}] Failed to register.\n` + error
            );
            setText("sip-status", "SIP Register Failure.");
            broadcastEvents("DISCONNECT", '', '');
            notifyCallEvent({ type: "register_error" });
          });
      })
      .catch((error: Error) => {
        app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to connect`);
        app_log(LOG_LEVEL_ERROR, error);
        app_alert(LOG_LEVEL_DEBUG, `[${user.id}] Failed to connect.\n` + error);
        setText("sip-status", "SIP Connect Failure.");
        broadcastEvents("DISCONNECT", '', '');
        notifyCallEvent({ type: "register_error" });
      });
  };

  connectAndRegister();

  return user;
}

function buildPingUser(
  webSocketServer: string,
  aor: string,
  displayName: string,
  password: string
): SimpleUser {
  app_log(
    LOG_LEVEL_DEBUG,
    `[softphone][pinguser] Creating "${displayName}" <${aor}>...`
  );

  const options: Web.SimpleUserOptions = {
    reconnectionAttempts: 1,
    aor,
    media: {
      remote: {
        audio: getAudio("remoteAudio"),
      },
    },
    userAgentOptions: {
      logLevel: !isLogEnabled
        ? "error"
        : userConfig && userConfig["log_level"] === LOG_LEVEL_DEBUG
        ? "debug"
        : "error",
      displayName,
      authorizationPassword: password,
    },
  };

  const user = new SimpleUserWithDataChannel(webSocketServer, options);

  const delegate: Web.SimpleUserDelegate = {
    onRegistered: makeRegisteredCallback(user),
    onServerConnect: makeServerConnectCallback(user),
    onServerDisconnect: makeServerDisconnectCallback(user),
  };
  user.delegate = delegate;

  connectAndRegisterPingUser(user);

  return user;
}

const connectAndRegisterPingUser = (user: any) => {
  user
    .connect()
    .then(() => {
      user
        .register(undefined, {
          requestDelegate: {
            onReject: (response: { message: { reasonPhrase: any } }) => {
              app_log(
                LOG_LEVEL_ERROR,
                `[pinguser][${user.id}] REGISTER rejected`
              );
              let message = `Registration of "${user.id}" rejected.\n`;
              message += `[pinguser] Reason: ${response.message.reasonPhrase}\n`;
              app_alert(LOG_LEVEL_DEBUG, message);

              try {
                user.disconnect().catch((error: Error) => {});
              } catch (de) {
                app_log(LOG_LEVEL_ERROR, de);
              }
            },
          },
        })
        .then(() => {
          app_log(
            LOG_LEVEL_ERROR,
            `[pinguser][${user.id}] SIP Register Sent!!`
          );
          setText("sip-status", "SIP Registered");
          notifyCallEvent({
            type: "ping_registered",
            payload: { pingUser: user, softphone: softphone },
          });
        })
        .catch((error: Error) => {
          app_log(LOG_LEVEL_ERROR, `[pinguser][${user.id}] failed to register`);
          app_log(LOG_LEVEL_ERROR, error);
          app_alert(
            LOG_LEVEL_DEBUG,
            `[${user.id}] Failed to register.\n` + error
          );
          setText("sip-status", "SIP Register Failure.");
          try {
            user.disconnect().catch((error: Error) => {});
          } catch (de) {
            app_log(LOG_LEVEL_ERROR, de);
          }
        });
    })
    .catch((error: Error) => {
      app_log(LOG_LEVEL_ERROR, `[pinguser][${user.id}] failed to connect`);
      app_log(LOG_LEVEL_ERROR, error);
      app_alert(
        LOG_LEVEL_DEBUG,
        `[pinguser][${user.id}] Failed to connect.\n` + error
      );
      setText("sip-status", "SIP Connect Failure.");
      notifyCallEvent({ type: "ping_register_error" });
    });
};

var pingUser: any | null = null;
var user: any;

export const softphone = (
  user1: SimpleUserWithDataChannel,
  params: any,
  notifyCallEventCallback: any,
  notifyEventBasedCallEventCallback: any,
) => {
  if(!isEventBasedCallEnabled) {
    notifyCallEvent = notifyCallEventCallback;
  } else {
    notifyCallEvent = notifyEventBasedCallEventCallback
  }
  notifyEventBasedCallEvent = notifyEventBasedCallEventCallback

  if (params.type === "set-user") {
    if (user && user.isConnected()) {
      user.disconnect();
    }
    if (user?.userAgent?._transport) {
      user.userAgent._transport.disconnect();
    }
  }
  if (params.type === "set-ping-user") {
    userConfig = params.payload;

    return {
      pingUser: buildPingUser(
        userConfig.pingUser.outboundProxy,
        userConfig.pingUser.sipUri,
        userConfig.pingUser.displayName,
        userConfig.pingUser.password
      ),
      connectAndRegisterPingUser: connectAndRegisterPingUser,
    };
  }
  if (params.type === "set-user" && params.payload["status"]) {
    userConfig = params.payload;
    user = buildUser(
      userConfig.outboundProxy,
      userConfig.sipUri,
      userConfig.displayName,
      userConfig.password,
      userConfig.iceServers,
    );

    if (!user) {
      app_log(LOG_LEVEL_ERROR, "Something went wrong");
    }
  }

  if (user && params.type === "accept") {
    broadcastEvents("OK",user?.session?.incomingInviteRequest?.message?.headers?.To[0].raw,'');
    if(user?.session?.state !== 'Initial') return;
    const sessionDescriptionHandlerOptions: SessionDescriptionHandlerOptions = {};
    user
      .answer({ sessionDescriptionHandlerOptions })
      .then(() => {
        show("call-panel");
        setText("call-status", "Onging Call");
        const infoData = { ack: { answered: user?.session?.state }}
        notifyCallEvent({
          type: "ongoing",
          payload: { remote_number: getRemoteNumber(user) },
          event_info: infoData
        });
        broadcastEvents("ACK",user?.session?.incomingInviteRequest?.message?.headers?.To[0].raw,'');
      })
      .catch((error: Error) => {
        app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to answer call`);
        app_log(LOG_LEVEL_ERROR, error);
        app_alert(
          LOG_LEVEL_DEBUG,
          `[${user.id}] Failed to answer call.\n` + error
        );
        setText("call-status", "Call answer error");
        notifyCallEvent({ type: "error_accept_reject" });
      });
  }

  if (user && params.type === "reject") {
    allEventsInfo = {};
    if(user?.session?.state !== 'Initial') return;
    user
      .decline()
      .then(() => {
        notifyCallEvent({ type: "rejected" });
        broadcastEvents("Temporarily Unavailable",user?.session?.incomingInfoRequest?.message?.headers?.To[0].raw,'');
      })
      .catch((error: Error) => {
        app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to reject call`);
        app_log(LOG_LEVEL_ERROR, error);
        notifyCallEvent({ type: "error_accept_reject" });
      });
  }

  if (user && params.type == 'dialpad_input') {
    user.sendDTMF(params.value);
  }

  if (user && params.type === "mute") {
    user.mute();
    if (user.isMuted() === false) {
      app_log(
        LOG_LEVEL_ERROR,
        `[WebRTCClient][softphone] [${user.id}] failed to mute call`
      );
      app_alert(LOG_LEVEL_DEBUG, " Failed to mute call.\n");
    } else {
      notifyCallEvent({ type: "muted" });
    }
  }

  if (user && params.type === "unmute") {
    user.unmute();
    if (user.isMuted() === true) {
      app_log(
        LOG_LEVEL_ERROR,
        `[WebRTCClient][softphone] [${user.id}] failed to unmute call`
      );
      app_alert(LOG_LEVEL_DEBUG, "Failed to unmute call.\n");
    } else {
      notifyCallEvent({ type: "unmuted" });
    }
  }

  if (user && params.type === "hold") {
    user
      .hold()
      .then(() => {
        notifyCallEvent({ type: "holded" });
      })
      .catch((error: Error) => {
        app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to hold call`);
        app_log(LOG_LEVEL_ERROR, error);
        app_alert(LOG_LEVEL_DEBUG, "Failed to hold call.\n" + error);
      });
  }
  if (user && params.type === "unhold") {
    user
      .unhold()
      .then(() => {
        notifyCallEvent({ type: "unholded" });
      })
      .catch((error: Error) => {
        app_log(LOG_LEVEL_ERROR, `[${user.id}] failed to unhold call`);
        app_log(LOG_LEVEL_ERROR, error);
        app_alert(LOG_LEVEL_DEBUG, "Failed to unhold call.\n" + error);
      });
  }
  if (user && params.type === "hangup") {
    allEventsInfo = {};
    if(user?.session?.state !== 'Established') return; 
    user
      .hangup()
      .then(() => {
        notifyCallEvent({ type: "hangup_done" });
      })
      .catch((error: Error) => {
        app_log(
          LOG_LEVEL_ERROR,
          `[WebRTCClient][softphone] [${user.id}] failed to hangup call`
        );
        app_log(LOG_LEVEL_ERROR, error);
        notifyCallEvent({
          type: "hangup_done_with_error",
          payload: { error: error },
        });
      });
  }

  if (params.type === "deregister") {
  }
};

declare global {
  interface Window {
    webRTCClient: any;
    onSipEvent: any;
    showCallInfo: any;
  }
}

function getRemoteNumber(user: SimpleUserWithDataChannel) {
  return "";
}

function showPushNotifications(eventHeader: any) {

  // Fail-safe case, if browser does not support notifications.
  if (!("Notification" in window) && (window as any)['knowlarityNotificationNotSupported']) {
    notifyCallEvent({
      type: "notification",
      payload: { message: "This browser does not support system notifications.", variant: "error" }
    });
    (window as any)['knowlarityNotificationNotSupported'] = false;
    return;
  }

  // Helper function for notification.
  function notifyIfGranted() {
      const options = {
        body: "Incoming call",
        icon: toAbsoluteUrl("/media/sr/icons/dial-call.svg"), // Icon to be displayed in notification popup
      };
      const notification = new Notification("SR", options);
      notification.onclick = () => {
        window.parent.focus();
        setTimeout(() => { notification.close(); }, 5000);
      };
  }

  // If permission is already granted, then show notification.
  if (Notification.permission === "granted") {
    notifyIfGranted();
    return;
  }

  // If permission is set to default or ealrier denied, then prompt user to get permission.
  if (Notification.permission === "default" || Notification.permission === "denied") {
    // Promise to ask for Notification permission from user through oldSR tab.
    Notification.requestPermission().then((permission) => {
      if (permission == 'granted') {
        notifyIfGranted();
      } else if (permission == "denied" && (window as any)['knowlarityNotificationFlag']) {
        notifyCallEvent({
          type: "notification",
          payload: { message: "You need to grant permission to enable notifications.", variant: "error" }
        });
        // alert("You need to grant permission to enable notifications.");   // return if permission is not granted.
        (window as any)['knowlarityNotificationFlag'] = false;
      }
    })
  }

}

// Helper function to create call received callback
function makeCallReceivedCallback(user: SimpleUserWithDataChannel): () => void {
  return () => {
    const allHeaders: any = extractHeaders(user?.session?.request?.headers, "invited");
    setText("call-status", "Call received");
    const sessionDescriptionHandlerOptions: SessionDescriptionHandlerOptions = {};
    if(helpers.isFeatureEnabledProfile("remove_konnect_dependency") && helpers.isFeatureEnabledProfile('sip_url_flag')) {
      notifyCallEvent = notifyEventBasedCallEvent;
      isEventBasedCallEnabled = true;
    }
    const isNonCCAccount = allHeaders['invited']['Callsolution'] === 'c2c' || allHeaders['invited']['Callsolution'] === 'ibd';
    if(!helpers.isFeatureEnabledProfile("remove_konnect_dependency")) {
      helpers.wsKonnectSyncTracker('wsKey', allHeaders['invited']['Calluuid']);
    }
    notifyCallEvent({
      type: "invited",
      payload: { v: "21", remote_number: getRemoteNumber(user) },
      event_info: allHeaders
    });
    if(helpers.isFeatureEnabledProfile('sip_url_flag')) 
    try { showPushNotifications(allHeaders['invited']); } catch (error) { console.log(error, 'Notification failed, Something went wrong !!') } 
    broadcastEvents("INVITE",user?.session?.incomingInviteRequest?.message?.headers?.To[0].raw,'');
  };
}

function makeCallInfoCallback(user:any, info:any) {
  // const allHeaders = extractHeaders(info.request.headers, "info");
  const infoData = { info: { additionalinfo: info }}
  notifyCallEvent({
    type: "info",
    event_info: infoData
  });
  return true;
}

function makeCallCreatedCallback(user: any): () => void {
  return () => {};
}

function makeCallHangupCallback(user: any): () => void {
  return () => {
    notifyCallEvent({ type: "hangup" });
  };
}

function makeRegisteredCallback(user: any): () => void {
  return () => {};
}

function makeUnregisteredCallback(user: any): () => void {
  return () => {
    broadcastEvents("DISCONNECT", '', '');
    notifyCallEvent({ type: "unregistered" });
  };
}

function makeServerConnectCallback(user: any): () => void {
  return () => {};
}

function makeServerDisconnectCallback(user: any): () => void {
  return (error?: Error) => {
    if (error) {
      app_alert(
        LOG_LEVEL_DEBUG,
        `[${user.id}] Server disconnected.\n` + error.message
      );
    }
    notifyCallEvent({ type: "unregistered" });
  };
}
